import { PAGE_PERMISSION_CODES, PERMISSION_CODES } from '@core-ui/common';
import { FeatureProtectedView } from '@moniebook-web/components/molecules/feature-protected-view';
import { ProtectedView } from '@moniebook-web/components/molecules/protected-view';
import { Outlet, type RouteObject } from 'react-router';

import { StyleScope } from '@/features/shared/style-scope';

const BUSINESS_FEATURE_FLAG_ENABLE_COLLECTION_ACCOUNT =
    'moniebook.pos.enableCollectionAccount';

export const businessMgtRoutes: RouteObject[] = [
    {
        path: '',
        element: <StyleScope name='business-mgt-ui' />,
        children: [
            {
                path: 'onboarding/subscription',
                async lazy() {
                    const { subscription } = await unwrapFeatures();
                    return subscription().then((m) => ({
                        Component: m.default,
                    }));
                },
            },
            {
                path: 'activity-logs',
                element: (
                    <ProtectedView
                        accessCodeList={PAGE_PERMISSION_CODES.ACTIVITY_LOG}
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { auditLogs } = await unwrapFeatures();
                            return auditLogs().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'onboarding/assign-moniepoint-account',
                async lazy() {
                    const { assignMoniepointAccount } = await unwrapFeatures();
                    return assignMoniepointAccount().then((m) => ({
                        Component: m.default,
                    }));
                },
            },
            {
                path: 'collections',
                element: (
                    <FeatureProtectedView
                        flag={BUSINESS_FEATURE_FLAG_ENABLE_COLLECTION_ACCOUNT}>
                        <Outlet />
                    </FeatureProtectedView>
                ),
                children: [
                    {
                        path: '',
                        element: (
                            <ProtectedView
                                accessCodeList={[
                                    PERMISSION_CODES.VIEW_COLLECTION_ACCOUNT,
                                ]}
                            />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { collectionsAccountList } =
                                        await unwrapFeatures();
                                    return collectionsAccountList().then(
                                        (m) => ({ Component: m.default }),
                                    );
                                },
                            },
                        ],
                    },
                    {
                        path: ':accountId/transactions',
                        element: (
                            <ProtectedView
                                accessCodeList={[
                                    PERMISSION_CODES.VIEW_COLLECTION_ACCOUNT_TRANSACTION,
                                ]}
                                grantAccessIfAny
                            />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { collectionsAccountHistory } =
                                        await unwrapFeatures();
                                    return collectionsAccountHistory().then(
                                        (m) => ({ Component: m.default }),
                                    );
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'settings/branches',
                element: (
                    <ProtectedView
                        accessCodeList={PAGE_PERMISSION_CODES.BRANCHES}
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { branches } = await unwrapFeatures();
                            return branches().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'create',
                        async lazy() {
                            const { createBranch } = await unwrapFeatures();
                            return createBranch().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: ':id/edit',
                        async lazy() {
                            const { updateBranch } = await unwrapFeatures();
                            return updateBranch().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                ],
            },
            {
                path: 'settings/staff',
                element: (
                    <ProtectedView
                        grantAccessIfAny
                        accessCodeList={[
                            PERMISSION_CODES.MANAGE_STAFF,
                            PERMISSION_CODES.MANAGE_ROLE,
                        ]}
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { staffList } = await unwrapFeatures();
                            return staffList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'create',
                        element: (
                            <ProtectedView
                                accessCodeList={[PERMISSION_CODES.CREATE_STAFF]}
                            />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { inviteStaff } =
                                        await unwrapFeatures();
                                    return inviteStaff().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: ':id/edit',
                        element: (
                            <ProtectedView
                                accessCodeList={[PERMISSION_CODES.EDIT_STAFF]}
                            />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { updateStaff } =
                                        await unwrapFeatures();
                                    return updateStaff().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
            {
                path: 'settings/roles',
                element: (
                    <ProtectedView
                        accessCodeList={[PERMISSION_CODES.MANAGE_ROLE]}
                        grantAccessIfAny
                    />
                ),
                children: [
                    {
                        path: '',
                        async lazy() {
                            const { rolesList } = await unwrapFeatures();
                            return rolesList().then((m) => ({
                                Component: m.default,
                            }));
                        },
                    },
                    {
                        path: 'create',
                        async lazy() {
                            const { customRoleGuard } = await unwrapFeatures();
                            return customRoleGuard().then((m) => ({
                                Component: m.default,
                            }));
                        },
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { createRole } =
                                        await unwrapFeatures();
                                    return createRole().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                    {
                        path: ':id/edit',
                        element: (
                            <ProtectedView
                                accessCodeList={[PERMISSION_CODES.EDIT_ROLE]}
                            />
                        ),
                        children: [
                            {
                                path: '',
                                async lazy() {
                                    const { updateRole } =
                                        await unwrapFeatures();
                                    return updateRole().then((m) => ({
                                        Component: m.default,
                                    }));
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
];

async function unwrapFeatures() {
    const features = await import('businessMgt/features');

    return features.default;
}
